/**
 * The app shell's own state (D317): the update bar and the guide. It used to live on the old
 * game's store; the idle app needs only this much of it.
 */
import { create } from 'zustand';
import type { CheckResult } from '../game/updater.ts';

/** What the shell needs from `game/updater.ts`, so this does not import the PWA plugin. */
export interface UpdaterHandle {
  requestApply(): Promise<'applied' | 'not-ready' | 'not-safe'>;
  checkNow(): Promise<CheckResult>;
}

let updater: UpdaterHandle | null = null;

/** Wired up once from `main.tsx`, after the service worker registers. */
export function setUpdaterHandle(h: UpdaterHandle): void {
  updater = h;
}

export interface Shell {
  /** A new build is waiting and the update bar shows (D47). */
  updateReady: boolean;
  /** The version the server last said it runs. */
  serverVersion: string | null;
  /** Changes not yet saved, which hold an update back. The idle app saves as it goes. */
  pending: number;
  /** The guide page open now, or null when the guide is shut. */
  guidePage: string | null;
  setUpdateReady: (ready: boolean) => void;
  setServerVersion: (version: string) => void;
  applyUpdate: () => Promise<void>;
  openGuide: (page: string) => void;
  closeGuide: () => void;
}

export const useShell = create<Shell>((set) => ({
  updateReady: false,
  serverVersion: null,
  pending: 0,
  guidePage: null,
  setUpdateReady: (ready) => set({ updateReady: ready }),
  setServerVersion: (version) => set({ serverVersion: version }),
  applyUpdate: async () => {
    await updater?.requestApply();
  },
  openGuide: (page) => set({ guidePage: page }),
  closeGuide: () => set({ guidePage: null }),
}));
